/**
 * PC-v2.2-C2.3-B — «REGISTRO DE LIMPIEZAS» (E3) EN EXCEL
 *
 * Un bloque plegado junto al calendario (portal) o al Planner (gestión interna) que descarga el registro de
 * limpiezas y verificaciones de un periodo: qué se planificó, qué se hizo, cuándo, quién lo hizo y quién lo verificó,
 * con su historial. Qué columnas salen y sobre qué centros lo decide el servidor (perspectiva, permisos y ámbito); el
 * navegador sólo dice el periodo y el centro.
 *
 *   · El periodo empieza siendo el de la vista y se puede ampliar hasta 92 días (ambos incluidos) sin tocar el
 *     calendario. Si la vista cambia, vuelve a ser el de la vista.
 *   · Centro: el de la vista o, si hay más de uno, todos (los de la empresa del cliente; en gestión interna, los del
 *     cliente que alcanza quien descarga).
 *   · Sólo se pinta si la sesión tiene los dos permisos del informe (el servidor los vuelve a exigir: 403).
 */
import { useEffect, useId, useState, type ReactNode } from 'react';

import { useSesion } from '../contexto.tsx';
import { BotonExportarExcel } from './exportar-excel.tsx';

export const MAX_DIAS_E3 = 92;

const RE_FECHA = /^\d{4}-\d{2}-\d{2}$/;
function dias(desde: string, hasta: string): number {
  return Math.round((Date.parse(`${hasta}T00:00:00Z`) - Date.parse(`${desde}T00:00:00Z`)) / 86_400_000) + 1;
}

/** Por qué no se puede pedir ese periodo, o null si se puede. */
export function motivoPeriodoE3(desde: string, hasta: string): string | null {
  if (!RE_FECHA.test(desde) || !RE_FECHA.test(hasta) || Number.isNaN(Date.parse(desde)) || Number.isNaN(Date.parse(hasta))) return 'Indica las dos fechas del periodo.';
  if (hasta < desde) return '«Desde» no puede ser posterior a «Hasta».';
  if (dias(desde, hasta) > MAX_DIAS_E3) return `El registro admite como mucho ${String(MAX_DIAS_E3)} días.`;
  return null;
}

/** ¿Puede esta sesión exportar el registro en esa perspectiva? (las dos llaves de C2.3-A) */
export function puedeExportarRegistro(puede: (p: string) => boolean, perspectiva: 'PORTAL' | 'INTERNA'): boolean {
  return perspectiva === 'PORTAL'
    ? puede('informes_operativos:exportar_propios') && puede('ots:leer_propias')
    : puede('informes_operativos:exportar') && puede('ots:leer');
}

export function ExportarRegistroLimpiezas({ perspectiva, idCliente, idCentro, varios, desdeVista, hastaVista }: {
  perspectiva: 'PORTAL' | 'INTERNA';
  /** Gestión interna: el cliente del centro elegido (obligatorio en el servidor). */
  idCliente?: string;
  /** El centro de la vista. */
  idCentro: string;
  /** ¿Hay más de un centro que elegir? Si no, no se ofrece «todos». */
  varios: boolean;
  desdeVista: string;
  hastaVista: string;
}): ReactNode {
  const { puede } = useSesion();
  const id = useId();
  const [desde, setDesde] = useState(desdeVista);
  const [hasta, setHasta] = useState(hastaVista);
  const [todos, setTodos] = useState(false);
  useEffect(() => { setDesde(desdeVista); setHasta(hastaVista); }, [desdeVista, hastaVista]);
  useEffect(() => { setTodos(false); }, [idCentro]);
  if (!puedeExportarRegistro(puede, perspectiva)) return null;

  const motivo = motivoPeriodoE3(desde, hasta);
  const parametros = new URLSearchParams({
    ...(perspectiva === 'INTERNA' && idCliente !== undefined ? { idCliente } : {}),
    desde, hasta,
    ...(todos ? {} : { idCentro }),
  });
  const base = perspectiva === 'PORTAL' ? '/api/portal/informes/registro-limpiezas/exportar' : '/api/informes/registro-limpiezas/exportar';
  const ruta = idCentro === '' || (perspectiva === 'INTERNA' && (idCliente ?? '') === '') ? null : `${base}?${parametros.toString()}`;
  const textoTodos = perspectiva === 'PORTAL' ? 'Todos mis centros' : 'Todos los centros del cliente';

  return (
    <details className="panel registro-limpiezas">
      <summary className="panel-cuerpo">
        <strong>Registro de limpiezas y verificaciones</strong>
        <span className="pequena tenue"> · Excel con lo planificado, lo hecho, quién lo hizo y quién lo verificó, con su historial</span>
      </summary>
      <div className="panel-cuerpo fila" style={{ gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div className="campo">
          <label htmlFor={`${id}-desde`}>Desde (fecha prevista)</label>
          <input id={`${id}-desde`} type="date" value={desde} onChange={(e) => setDesde(e.target.value)} />
        </div>
        <div className="campo">
          <label htmlFor={`${id}-hasta`}>Hasta</label>
          <input id={`${id}-hasta`} type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} />
        </div>
        {varios && (
          <div className="campo">
            <label htmlFor={`${id}-ambito`}>Centros</label>
            <select id={`${id}-ambito`} value={todos ? 'todos' : 'este'} onChange={(e) => setTodos(e.target.value === 'todos')}>
              <option value="este">Sólo el centro elegido</option>
              <option value="todos">{textoTodos}</option>
            </select>
          </div>
        )}
        <BotonExportarExcel
          ruta={ruta} motivoDeshabilitado={motivo}
          etiqueta="Exportar registro"
          ayuda={`Registro de limpiezas del periodo elegido (hasta ${String(MAX_DIAS_E3)} días)`}
        />
      </div>
      {motivo !== null && <p className="panel-cuerpo pequena" role="alert" style={{ marginTop: 0 }}>{motivo}</p>}
    </details>
  );
}
